// frontend/src/pages/CartaoDetailPage/CartaoDetailPage.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    getFaturaDetail,
    deleteTransacaoFatura,
    reabrirFatura,
    deletePagamentoParcial,
    getCartoes,
    getCartoesAdicionais,
    createCartaoAdicional,
    updateCartaoAdicional,
    deleteCartaoAdicional,
    getGastosPortadorFatura,
    gerarContaReceber,
    reactivateCartaoAdicional,
    getFaturaContextoInicial
} from '../../services/cardService';
import { getContas } from '../../services/accountService';
import { getCategorias } from '../../services/categoryService';
import { createRecorrenciaExcecao, confirmarOcorrenciaRecorrencia } from '../../services/recurrenceService';
import { useDataRefresh } from '../../contexts/DataRefreshContext';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { prefixoObservacaoPontosFatura } from '../../utils/estimativaPontosCartao';
import { getTransacoesMilhas, deleteTransacaoMilhas } from '../../services/milhasService';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import ParcelamentosFuturosChart from '../../components/CartoesAnalytics/ParcelamentosFuturosChart';
import UsoPorDiaChart from '../../components/CartoesAnalytics/UsoPorDiaChart';
import dayjs from 'dayjs';

// Ant Design Imports
import {
    Layout, Row, Col, Card, Statistic, Button, Table, Space, Tag, List,
    Tooltip, Modal as AntModal, Input, Select, message, Form, Spin,
    Tabs, Descriptions, Dropdown, Typography, Switch, InputNumber, DatePicker,
    Divider, Checkbox
} from 'antd';
import {
    ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined, DollarCircleOutlined,
    UndoOutlined, SearchOutlined, DownOutlined, InboxOutlined, CalendarOutlined,
    SyncOutlined, CheckCircleFilled, RollbackOutlined
} from '@ant-design/icons';
import CurrencyInput from 'react-currency-input-field';

// Componentes e Modais
import SeletorDeMes from '../../components/SeletorDeMes/SeletorDeMes';
import CartaoDespesaModal from '../../components/CartaoDespesaModal/CartaoDespesaModal.jsx';
import EstornoModal from '../../components/EstornoModal/EstornoModal.jsx';
import PagarFaturaModal from '../../components/PagarFaturaModal/PagarFaturaModal.jsx';
import PagamentoParcialModal from '../../components/PagamentoParcialModal/PagamentoParcialModal.jsx';
import LogoManager from '../../components/LogoManager/LogoManager';
import { getMobileFullscreenModalProps } from '../../utils/mobileModal';

import './CartaoDetailPage.css';

const { Content } = Layout;
const { Option } = Select;
const { Text } = Typography;

const dataExibicao = (item) => item?.data_compra || item?.data_vencimento;

const formatCurrency = (value) => {
    const number = parseFloat(value) || 0;
    return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const getStatusTag = (status) => {
    switch (status) {
        case 'PAGA': return <Tag color="green">PAGA</Tag>;
        case 'FECHADA': return <Tag color="volcano">FECHADA</Tag>;
        case 'ABERTA': return <Tag color="blue">ABERTA</Tag>;
        default: return <Tag>{status}</Tag>;
    }
};

const ProjetadaEditModal = ({ isOpen, onClose, onSave, projetado, isMobile }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && projetado) {
            form.setFieldsValue({
                nova_descricao: projetado.descricao,
                novo_valor: parseFloat(projetado.valor),
                nova_data: dayjs(projetado.data_vencimento),
            });
        }
    }, [isOpen, projetado, form]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            // eslint-disable-next-line no-unused-vars
            const { nova_categoria, ...validValues } = values;

            const payload = {
                recorrencia: projetado.recorrencia_id,
                data_original_ocorrencia: dayjs(projetado.data_original_ocorrencia).format('YYYY-MM-DD'),
                ...validValues,
                nova_data: validValues.nova_data ? validValues.nova_data.format('YYYY-MM-DD') : null,
            };

            await createRecorrenciaExcecao(payload);
            onSave();
        } catch (error) {
            console.error("Erro ao salvar exceção:", error);
            AntModal.error({ 
                title: 'Erro', 
                content: getErrorMessage(error, 'Não foi possível salvar a alteração.') 
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <AntModal
            title="Editar Lançamento Previsto"
            open={isOpen}
            onCancel={onClose}
            onOk={handleSave}
            confirmLoading={loading}
            okText="Salvar Alteração"
            cancelText="Cancelar"
            okButtonProps={{ block: isMobile, size: isMobile ? 'large' : 'middle' }}
            cancelButtonProps={{ block: isMobile, size: isMobile ? 'large' : 'middle' }}
            {...getMobileFullscreenModalProps(isMobile)}
            width={isMobile ? undefined : 520}
        >
            <Form form={form} layout="vertical" size={isMobile ? 'large' : 'middle'}>
                <p>Você está editando apenas a recorrência de <strong>{projetado ? dayjs(projetado.data_vencimento).format('DD/MM/YYYY') : ''}</strong>.</p>
                <Form.Item name="nova_descricao" label="Descrição">
                    <Input />
                </Form.Item>
                <Form.Item name="novo_valor" label="Valor">
                    <InputNumber
                        style={{ width: '100%' }}
                        precision={2}
                        step={0.01}
                        inputMode="decimal"
                        formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)}
                        parser={(value) => {
                            if (!value) return null;
                            const onlyNumbers = value.replace(/[^\d]/g, '');
                            if (!onlyNumbers) return null;
                            return parseInt(onlyNumbers, 10) / 100;
                        }}
                    />
                </Form.Item>
                <Form.Item name="nova_data" label="Nova Data">
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
            </Form>
        </AntModal>
    );
};

const CartaoDetailPage = () => {
    const { id: cartaoId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { temModuloMilhas } = useAuth();
    
    const [currentDate, setCurrentDate] = useState(null);

    const [modal, modalContextHolder] = AntModal.useModal();
    const [messageApi, messageContextHolder] = message.useMessage();

    // Estados de dados
    const [fatura, setFatura] = useState(null);
    const [transacoes, setTransacoes] = useState([]);
    const [cartoes, setCartoes] = useState([]);
    const [cartoesAdicionais, setCartoesAdicionais] = useState([]);
    const [gastosPortador, setGastosPortador] = useState(null);
    const [categorias, setCategorias] = useState([]);

    // Estados de UI
    const [loading, setLoading] = useState(true);
    const [loadingCartoes, setLoadingCartoes] = useState(false);
    const [loadingAdicionais, setLoadingAdicionais] = useState(false);
    const [filtroDescricao, setFiltroDescricao] = useState('');
    const [filtroPortador, setFiltroPortador] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
    });

    // Estados de controle de modais
    const [isDespesaModalOpen, setIsDespesaModalOpen] = useState(false);
    const [isEstornoModalOpen, setIsEstornoModalOpen] = useState(false);
    const [isPagarFaturaModalOpen, setIsPagarFaturaModalOpen] = useState(false);
    const [isPagamentoParcialModalOpen, setIsPagamentoParcialModalOpen] = useState(false);
    const [isContaReceberModalOpen, setIsContaReceberModalOpen] = useState(false);
    const [isCartaoAdicionalModalOpen, setIsCartaoAdicionalModalOpen] = useState(false);
    const [isReabrirModalOpen, setIsReabrirModalOpen] = useState(false);
    const [pontosPendentesReabrir, setPontosPendentesReabrir] = useState([]);
    const [excluirPontosPendentes, setExcluirPontosPendentes] = useState(true);
    const [loadingPontosReabrir, setLoadingPontosReabrir] = useState(false);
    const [reabrindoFatura, setReabrindoFatura] = useState(false);
    const [isProjetadaModalOpen, setIsProjetadaModalOpen] = useState(false);
    const [isEditScopeModalOpen, setIsEditScopeModalOpen] = useState(false);
    const [isDeleteScopeModalOpen, setIsDeleteScopeModalOpen] = useState(false);

    // Estados para edição/criação
    const [transacaoToEdit, setTransacaoToEdit] = useState(null);
    const [editingProjetado, setEditingProjetado] = useState(null);
    const [cartaoAdicionalToEdit, setCartaoAdicionalToEdit] = useState(null);
    const [portadorParaCobranca, setPortadorParaCobranca] = useState(null);
    const [editScope, setEditScope] = useState('apenas_esta');

    const { refreshKey } = useDataRefresh();

    // Responsividade
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const carregarCartoes = async () => {
            setLoadingCartoes(true);
            try {
                const data = await getCartoes({ status: 'ATIVO' });
                setCartoes(data);
            } catch (error) {
                messageApi.error(getErrorMessage(error, 'Não foi possível carregar os cartões.'));
            } finally {
                setLoadingCartoes(false);
            }
        };
        carregarCartoes();
    }, [messageApi]);

    const CartaoAdicionalModalAntD = ({ isOpen, onClose, onSave, cartaoPrincipalId, cartaoToEdit }) => {
        const [form] = Form.useForm();
        const [isLoading, setIsLoading] = useState(false);

        useEffect(() => {
            if (isOpen) {
                if (cartaoToEdit) {
                    form.setFieldsValue({ portador: cartaoToEdit.portador });
                } else {
                    form.resetFields();
                }
            }
        }, [cartaoToEdit, isOpen, form]);

        const handleSubmit = async (values) => {
            setIsLoading(true);
            const data = { ...values, cartao_principal: cartaoPrincipalId };
            try {
                if (cartaoToEdit) {
                    await updateCartaoAdicional(cartaoToEdit.id, data);
                } else {
                    await createCartaoAdicional(data);
                }
                messageApi.success(`Cartão adicional ${cartaoToEdit ? 'atualizado' : 'criado'} com sucesso!`);
                onSave();
            } catch (err) {
                messageApi.error(getErrorMessage(err, 'Erro ao salvar cartão adicional.'));
            } finally {
                setIsLoading(false);
            }
        };

        return (
            <AntModal 
                title={cartaoToEdit ? 'Editar Cartão Adicional' : 'Novo Cartão Adicional'} 
                open={isOpen} 
                onCancel={onClose}
                footer={[
                    <Button key="back" onClick={onClose} disabled={isLoading} block={isMobile} size={isMobile ? 'large' : 'middle'}>Cancelar</Button>,
                    <Button key="submit" type="primary" loading={isLoading} onClick={() => form.submit()} block={isMobile} size={isMobile ? 'large' : 'middle'}>Salvar</Button>,
                ]}
                {...getMobileFullscreenModalProps(isMobile)}
                width={isMobile ? undefined : 520}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit} size={isMobile ? 'large' : 'middle'}>
                    <Form.Item name="portador" label="Nome do Portador" rules={[{ required: true, message: 'O nome do portador é obrigatório.' }]}>
                        <Input placeholder="Ex: João da Silva" />
                    </Form.Item>
                </Form>
            </AntModal>
        );
    };

    const GerarContaReceberModalAntD = ({ isOpen, onClose, onSave, fatura, portador }) => {
        const [form] = Form.useForm();
        const [contas, setContas] = useState([]);
        const [isLoading, setIsLoading] = useState(false);

        useEffect(() => {
            if (isOpen) {
                if (portador && fatura) {
                    form.setFieldsValue({
                        valor: portador.total_gasto,
                        data_vencimento: dayjs(fatura.data_vencimento),
                        conta_id: null
                    });
                }
                getContas().then(data => setContas(data.results || data)).catch(() => messageApi.error('Não foi possível carregar as contas.'));
            }
        }, [isOpen, portador, fatura, form]);

        const handleSubmit = async (values) => {
            setIsLoading(true);
            const payload = {
                fatura_id: fatura.id,
                cartao_adicional_id: portador.id,
                conta_id: values.conta_id,
                valor: values.valor,
                data_vencimento: values.data_vencimento.format('YYYY-MM-DD'),
            };
            try {
                await gerarContaReceber(payload);
                messageApi.success('Conta a receber gerada! Use o botão WhatsApp na Agenda de Transações para cobrar.');
                onSave();
            } catch (err) {
                messageApi.error(getErrorMessage(err, 'Falha ao gerar a conta a receber.'));
            } finally {
                setIsLoading(false);
            }
        };

        if (!portador) return null;

        return (
            <AntModal 
                title={`Gerar Conta a Receber de ${portador.portador}`} 
                open={isOpen} 
                onCancel={onClose}
                onOk={() => form.submit()} 
                confirmLoading={isLoading} 
                okText="Confirmar" 
                cancelText="Cancelar"
                okButtonProps={{ block: isMobile, size: isMobile ? 'large' : 'middle' }}
                cancelButtonProps={{ block: isMobile, size: isMobile ? 'large' : 'middle' }}
                {...getMobileFullscreenModalProps(isMobile)}
                width={isMobile ? undefined : 520}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit} size={isMobile ? 'large' : 'middle'}>
                    <Form.Item name="valor" label="Valor a Receber" rules={[{ required: true, message: 'O valor é obrigatório' }]}>
                        <CurrencyInput 
                            placeholder="R$ 0,00" 
                            decimalSeparator="," 
                            groupSeparator="." 
                            prefix="R$ " 
                            className="ant-input"
                            allowNegativeValue={false}
                            decimalsLimit={2}
                            onValueChange={(value, name) => form.setFieldsValue({ [name]: value })}
                            style={{ height: isMobile ? 40 : 32, fontSize: isMobile ? 16 : 14 }}
                        />
                    </Form.Item>
                    <Form.Item name="data_vencimento" label="Data Prevista" rules={[{ required: true, message: 'A data é obrigatória' }]}>
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="conta_id" label="Creditar na conta:" rules={[{ required: true, message: 'Selecione uma conta' }]}>
                        <Select placeholder="Selecione uma conta...">{contas.map(c => <Option key={c.id} value={c.id}>{c.nome}</Option>)}</Select>
                    </Form.Item>
                </Form>
            </AntModal>
        );
    };

    const carregarDadosFatura = useCallback(async (data) => {
        setLoading(true);
        try {
            const ano = data.getFullYear();
            const mes = data.getMonth() + 1;
            
            const [faturaResponse, allCategorias] = await Promise.all([
                getFaturaDetail(cartaoId, ano, mes),
                getCategorias()
            ]);
            
            const novaFatura = faturaResponse.fatura;
            setFatura(novaFatura);
            setTransacoes(faturaResponse.transacoes);
            setCategorias(allCategorias);

            if (novaFatura && novaFatura.id) {
                const gastosResponse = await getGastosPortadorFatura(novaFatura.id);
                setGastosPortador(gastosResponse);
            } else {
                setGastosPortador(null);
            }
        } catch (error) {
            console.error("Erro ao carregar dados da fatura:", error);
            messageApi.error(getErrorMessage(error, "Não foi possível carregar os dados da fatura."));
            setFatura(null); 
            setTransacoes([]); 
            setGastosPortador(null);
            setCategorias([]);
        } finally {
            setLoading(false);
        }
    }, [cartaoId, messageApi]);

    const carregarCartoesAdicionais = useCallback(async () => {
        setLoadingAdicionais(true);
        const status = showArchived ? 'ARQUIVADO' : null;
        try {
            const data = await getCartoesAdicionais(cartaoId, status);
            setCartoesAdicionais(data);
        } catch (error) {
            messageApi.error(getErrorMessage(error, "Não foi possível carregar os cartões."));
        } finally {
            setLoadingAdicionais(false);
        }
    }, [cartaoId, showArchived, messageApi]);

    const anoQuery = searchParams.get('ano');
    const mesQuery = searchParams.get('mes');

    useEffect(() => {
        const fetchInitialContext = async () => {
            const anoParam = parseInt(anoQuery, 10);
            const mesParam = parseInt(mesQuery, 10);
            if (
                Number.isFinite(anoParam) &&
                Number.isFinite(mesParam) &&
                mesParam >= 1 &&
                mesParam <= 12
            ) {
                setCurrentDate(new Date(anoParam, mesParam - 1, 1));
                return;
            }

            try {
                const contexto = await getFaturaContextoInicial(cartaoId);
                setCurrentDate(new Date(contexto.ano, contexto.mes - 1, 1));
            } catch (error) {
                console.error("Erro ao buscar contexto inicial:", error);
                messageApi.error(getErrorMessage(error, "Não foi possível determinar a fatura inicial. Carregando mês atual."));
                setCurrentDate(new Date());
            }
        };
        fetchInitialContext();
    }, [cartaoId, messageApi, anoQuery, mesQuery]);

    useEffect(() => {
        if (currentDate) {
            carregarDadosFatura(currentDate);
        }
    }, [currentDate, carregarDadosFatura, refreshKey]);

    useEffect(() => {
        carregarCartoesAdicionais();
    }, [carregarCartoesAdicionais, refreshKey]);

    useEffect(() => {
        const handleTransactionUpdate = () => {
            if (currentDate) {
                carregarDadosFatura(currentDate);
                carregarCartoesAdicionais();
            }
        };
        window.addEventListener('transaction-updated', handleTransactionUpdate);
        return () => {
            window.removeEventListener('transaction-updated', handleTransactionUpdate);
        };
    }, [currentDate, carregarDadosFatura, carregarCartoesAdicionais]);
    
    
    const handleTableChange = (newPagination) => {
        setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        });
    };

    const handleCloseAndRefresh = (shouldClose = true, successMessage = null) => {
        if (currentDate) {
            carregarDadosFatura(currentDate);
        }
        
        if (successMessage) messageApi.success(successMessage);

        if (shouldClose) {
            setIsDespesaModalOpen(false);
            setIsEstornoModalOpen(false);
            setIsPagarFaturaModalOpen(false);
            setIsPagamentoParcialModalOpen(false);
            setTransacaoToEdit(null);
        }
    };

    const handleCloseCartaoAdicionalModal = () => {
        setIsCartaoAdicionalModalOpen(false);
        setCartaoAdicionalToEdit(null);
        carregarCartoesAdicionais();
    };

    const handleCloseContaReceberModal = () => {
        setIsContaReceberModalOpen(false);
        setPortadorParaCobranca(null);
        if (currentDate) {
            carregarDadosFatura(currentDate);
        }
    };

    const handleEditClick = (record) => {
        if (record.item_type?.startsWith('transacao_projetada')) {
            setEditingProjetado(record);
            setIsProjetadaModalOpen(true);
            return;
        }
        if (record.tipo_especial === 'PAGAMENTO_FATURA') {
            messageApi.warning('Este tipo de lançamento não pode ser editado diretamente.');
            return;
        }

        if (record.compra_parcelada_id) { 
            setTransacaoToEdit(record);
            setIsEditScopeModalOpen(true);
        } else { 
            setTransacaoToEdit(record);
            if (record.categoria_nome === 'Pagamento Avulso de Fatura') {
                setIsPagamentoParcialModalOpen(true);
            } else if (record.tipo === 'despesa') {
                setIsDespesaModalOpen(true);
            } else if (record.tipo === 'receita') {
                setIsEstornoModalOpen(true);
            }
        }
    };
    
    const performDelete = async (transacaoId, scope = 'apenas_esta') => {
        try {
            await deleteTransacaoFatura(transacaoId, scope);
            messageApi.success('Lançamento excluído com sucesso.');
            carregarDadosFatura(currentDate);
        } catch (err) {
            messageApi.error(getErrorMessage(err, "Não foi possível excluir o lançamento."));
        }
    };
    
    const handleDeleteClick = (record) => {
        if (record.item_type?.startsWith('transacao_projetada')) {
            modal.confirm({
                title: 'Pular esta recorrência?',
                content: 'Tem certeza que deseja pular o lançamento previsto deste dia?',
                okText: 'Sim, pular',
                cancelText: 'Cancelar',
                onOk: async () => {
                    try {
                        await createRecorrenciaExcecao({
                            recorrencia: record.recorrencia_id,
                            data_original_ocorrencia: dayjs(record.data_original_ocorrencia).format('YYYY-MM-DD'),
                            pulada: true
                        });
                        messageApi.success('Recorrência pulada com sucesso!');
                        carregarDadosFatura(currentDate);
                    } catch (error) {
                         modal.error({ 
                             title: 'Erro', 
                             content: getErrorMessage(error, 'Ocorreu um erro ao tentar pular a recorrência.') 
                         });
                    }
                }
            });
            return;
        }

        if (record.categoria_nome === 'Pagamento Avulso de Fatura') {
            modal.confirm({
                title: 'Excluir Pagamento Parcial',
                content: `Deseja excluir este pagamento de ${formatCurrency(record.valor)}? A ação irá estornar o valor na conta de origem.`,
                onOk: async () => {
                    try {
                        await deletePagamentoParcial(record.id);
                        messageApi.success('Pagamento parcial excluído.');
                        carregarDadosFatura(currentDate);
                    } catch (err) { 
                        messageApi.error(getErrorMessage(err, "Falha ao excluir.")); 
                    }
                },
            });
            return;
        }
        if (record.tipo_especial === 'PAGAMENTO_FATURA') {
             messageApi.error('Para estornar, use a opção "Reabrir Fatura".');
             return;
        }

        if (record.compra_parcelada_id) { 
            setTransacaoToEdit(record);
            setIsDeleteScopeModalOpen(true);
        } else { 
            modal.confirm({
                title: 'Confirmar Exclusão',
                content: `Deseja excluir o lançamento "${record.descricao}"?`,
                onOk: () => performDelete(record.id),
            });
        }
    };
    
    const handleDeleteCartaoAdicional = (cartao) => {
        modal.confirm({
            title: 'Remover Cartão Adicional',
            content: `Deseja remover o cartão do portador "${cartao.portador}"? Se houver transações, ele será apenas arquivado.`,
            onOk: async () => {
                try {
                    const response = await deleteCartaoAdicional(cartao.id);
                    if (response.status === 204) {
                        messageApi.success('Cartão adicional excluído permanentemente.');
                    } else if (response.status === 200) {
                        messageApi.info('Cartão adicional arquivado com sucesso.');
                    }
                    carregarCartoesAdicionais();
                    if (fatura) {
                        const gastosResponse = await getGastosPortadorFatura(fatura.id);
                        setGastosPortador(gastosResponse);
                    }
                } catch (err) {
                    messageApi.error(getErrorMessage(err, "Falha ao remover o cartão."));
                }
            },
        });
    };
    
    const handleReativarCartao = (cartao) => {
        modal.confirm({
            title: 'Reativar Cartão Adicional',
            content: `Deseja reativar o cartão do portador "${cartao.portador}"?`,
            onOk: async () => {
                try {
                    await reactivateCartaoAdicional(cartao.id);
                    messageApi.success('Cartão adicional reativado com sucesso.');
                    carregarCartoesAdicionais();
                } catch (err) {
                    messageApi.error(getErrorMessage(err, "Falha ao reativar o cartão."));
                }
            }
        })
    }
    
    useEffect(() => {
        if (!isReabrirModalOpen || !fatura?.id || !temModuloMilhas) {
            if (!isReabrirModalOpen) {
                setPontosPendentesReabrir([]);
                setExcluirPontosPendentes(true);
            }
            return undefined;
        }

        let cancelled = false;
        setLoadingPontosReabrir(true);
        setExcluirPontosPendentes(true);

        (async () => {
            try {
                let list = await getTransacoesMilhas({
                    status: 'PENDENTE',
                    sub_tipo: 'PONTOS_CARTAO',
                    fatura_id: fatura.id,
                });
                if (!list.length) {
                    const nomeCartao = fatura.cartao_nome || fatura.cartao?.nome;
                    const prefixo = prefixoObservacaoPontosFatura({
                        nomeCartao,
                        mes: fatura.mes_referencia,
                        ano: fatura.ano_referencia,
                    });
                    const legado = await getTransacoesMilhas({
                        status: 'PENDENTE',
                        sub_tipo: 'PONTOS_CARTAO',
                    });
                    list = legado.filter((tx) => (tx.observacao || '').includes(prefixo));
                }
                if (!cancelled) setPontosPendentesReabrir(list);
            } catch {
                if (!cancelled) setPontosPendentesReabrir([]);
            } finally {
                if (!cancelled) setLoadingPontosReabrir(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isReabrirModalOpen, fatura, temModuloMilhas]);

    const handleConfirmReabrirFatura = async () => {
        if (!fatura?.id) return;
        setReabrindoFatura(true);
        try {
            await reabrirFatura(fatura.id);
            let successMsg = 'Fatura reaberta com sucesso.';
            if (excluirPontosPendentes && pontosPendentesReabrir.length > 0) {
                try {
                    await Promise.all(
                        pontosPendentesReabrir.map((tx) => deleteTransacaoMilhas(tx.id)),
                    );
                    successMsg += ' Lançamento(ns) de milhas pendente(s) excluído(s).';
                } catch (delErr) {
                    messageApi.warning(
                        getErrorMessage(
                            delErr,
                            'Fatura reaberta, mas falhou ao excluir o lançamento de milhas pendente.',
                        ),
                        8,
                    );
                    setIsReabrirModalOpen(false);
                    setPontosPendentesReabrir([]);
                    carregarDadosFatura(currentDate);
                    return;
                }
            }
            messageApi.success(successMsg);
            setIsReabrirModalOpen(false);
            setPontosPendentesReabrir([]);
            carregarDadosFatura(currentDate);
        } catch (err) {
            messageApi.error(getErrorMessage(err, "Falha ao reabrir a fatura."));
        } finally {
            setReabrindoFatura(false);
        }
    };
    
    const handleConfirmarProjetado = (projetado) => {
        modal.confirm({
            title: 'Confirmar Lançamento Previsto',
            content: 'Isso irá criar um lançamento real na fatura com os dados da projeção. Deseja continuar?',
            okText: 'Sim, Confirmar',
            onOk: async () => {
                try {
                    const payload = {
                        data_original_ocorrencia: dayjs(projetado.data_original_ocorrencia).format('YYYY-MM-DD'),
                        descricao: projetado.descricao,
                        valor: projetado.valor,
                        data_pagamento: dayjs(projetado.data_vencimento).format('YYYY-MM-DD'),
                    };
                    const recorrenciaId = projetado.recorrencia_id;
                    await confirmarOcorrenciaRecorrencia(recorrenciaId, payload);
                    messageApi.success('Lançamento confirmado com sucesso!');
                    carregarDadosFatura(currentDate);
                } catch (error) {
                    console.error('Erro ao confirmar lançamento previsto', error);
                    messageApi.error(getErrorMessage(error, 'Não foi possível confirmar o lançamento.'));
                }
            }
        });
    };

    const menuNovoLancamento = {
        items: [
            { key: 'despesa', label: 'Nova Despesa' },
            { key: 'estorno', label: 'Novo Estorno' },
            { key: 'parcial', label: 'Novo Pagamento Parcial' },
        ],
        onClick: ({ key }) => {
            setTransacaoToEdit(null);
            if (key === 'despesa') setIsDespesaModalOpen(true);
            if (key === 'estorno') setIsEstornoModalOpen(true);
            if (key === 'parcial') setIsPagamentoParcialModalOpen(true);
        }
    };
    
    const columns = [
        { 
            title: 'Data', 
            dataIndex: 'data_vencimento', 
            key: 'data', 
            sorter: (a, b) => new Date(dataExibicao(a)) - new Date(dataExibicao(b)), 
            render: (_text, record) => {
                const isProjetado = record.item_type?.startsWith('transacao_projetada');
                const data = dataExibicao(record);
                const dataFormatada = data
                    ? new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                    : '—';
                const conteudo = (
                    <Space>
                        {isProjetado && <Tooltip title="Lançamento Previsto"><CalendarOutlined /></Tooltip>}
                        {dataFormatada}
                    </Space>
                );
                if (record.compra_parcelada_id && record.data_compra) {
                    return <Tooltip title="Data da compra">{conteudo}</Tooltip>;
                }
                return conteudo;
            } 
        },
        { 
            title: 'Descrição', 
            dataIndex: 'descricao', 
            key: 'descricao', 
            sorter: (a, b) => a.descricao.localeCompare(b.descricao),
            render: (text, record) => (
                <Space>
                    {record.item_type?.startsWith('transacao_projetada') && <Tooltip title="Recorrência"><SyncOutlined /></Tooltip>}
                    {text}
                </Space>
            )
        },
        { title: 'Portador', dataIndex: 'portador', key: 'portador', sorter: (a, b) => (a.portador || 'Titular').localeCompare(b.portador || 'Titular'), render: (text) => text || 'Titular' },
        { title: 'Categoria', dataIndex: 'categoria_nome', key: 'categoria', sorter: (a, b) => (a.categoria_nome || '').localeCompare(b.categoria_nome || ''), render: (text) => text || <Tag>Sem Categoria</Tag> },
        { title: 'Valor', dataIndex: 'valor', key: 'valor', align: 'right', sorter: (a, b) => a.valor - b.valor, render: (text, record) => (<span className={record.tipo === 'despesa' ? 'valor-despesa' : 'valor-receita'}>{formatCurrency(text)}</span>)},
        { 
            title: 'Ações', 
            key: 'action', 
            align: 'center', 
            width: 120,
            render: (_, record) => {
                const isFaturaPaga = fatura?.status === 'PAGA';
                const isProjetado = record.item_type?.startsWith('transacao_projetada');

                return (
                    <Space size="small" style={{ display: 'flex', justifyContent: 'center' }}>
                        {isProjetado ? (
                            <Tooltip title="Confirmar Lançamento">
                                <Button type="text" style={{ color: 'green' }} icon={<CheckCircleFilled />} onClick={() => handleConfirmarProjetado(record)} disabled={isFaturaPaga} />
                            </Tooltip>
                        ) : (
                            <span style={{ display: 'inline-block', width: '32px' }} />
                        )}
                        <Tooltip title="Editar">
                            <Button type="text" icon={<EditOutlined />} onClick={() => handleEditClick(record)} disabled={isFaturaPaga} />
                        </Tooltip>
                        <Tooltip title={isProjetado ? "Pular Recorrência" : "Excluir"}>
                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteClick(record)} disabled={isFaturaPaga} />
                        </Tooltip>
                    </Space>
                );
            }
        },
    ];

    const filteredTransacoes = transacoes.filter(t => {
        const portadorNome = t.portador || 'Titular';
        const matchDescricao = filtroDescricao ? t.descricao.toLowerCase().includes(filtroDescricao.toLowerCase()) : true;
        const matchPortador = filtroPortador ? (filtroPortador === 'titular' ? portadorNome === 'Titular' : t.cartao_adicional === parseInt(filtroPortador)) : true;
        return matchDescricao && matchPortador;
    });

    const { valorReal, valorProjetado } = useMemo(() => {
        return transacoes.reduce((acc, t) => {
            const valor = parseFloat(t.valor) || 0;
            const isProjetado = t.item_type?.startsWith('transacao_projetada');
            const signal = t.tipo === 'despesa' ? 1 : -1;

            if (isProjetado) {
                acc.valorProjetado += (valor * signal);
            } else {
                acc.valorReal += (valor * signal);
            }
            return acc;
        }, { valorReal: 0, valorProjetado: 0 });
    }, [transacoes]);

    const cartaoSelecionado = useMemo(
        () => cartoes.find((c) => String(c.id) === String(cartaoId)),
        [cartoes, cartaoId]
    );
    const bandeiraSlug = cartaoSelecionado?.bandeira || fatura?.cartao_bandeira;
    
    // RENDERIZAÇÃO DAS TABS (CONTEÚDO)
    const renderFaturaContent = () => (
        <Row gutter={[24, 24]}>
            <Col xs={24} lg={16} order={isMobile ? 2 : 1}>
                <Card 
                    title="Lançamentos na Fatura" 
                    bordered={!isMobile}
                    bodyStyle={{ padding: isMobile ? '0' : '24px' }}
                    extra={
                        <Space>
                            {!isMobile && (
                                <Input placeholder="Filtrar..." prefix={<SearchOutlined />} value={filtroDescricao} onChange={(e) => setFiltroDescricao(e.target.value)} allowClear style={{width: 200}} />
                            )}
                            <Select 
                                value={filtroPortador} 
                                onChange={(v) => setFiltroPortador(v)} 
                                style={{ width: isMobile ? 140 : 180 }} 
                                placeholder="Portador" 
                                allowClear
                                size={isMobile ? 'small' : 'middle'}
                            >
                                <Option value="">Todos</Option><Option value="titular">Titular</Option>
                                {gastosPortador?.adicionais.map(c => <Option key={c.id} value={String(c.id)}>{c.portador}</Option>)}
                            </Select>
                        </Space>
                    }
                >
                    {isMobile && (
                        <div style={{ padding: '12px 16px' }}>
                             <Input placeholder="Buscar lançamento..." prefix={<SearchOutlined />} value={filtroDescricao} onChange={(e) => setFiltroDescricao(e.target.value)} allowClear size="large" />
                        </div>
                    )}

                    {isMobile ? (
                        <List
                            dataSource={filteredTransacoes}
                            locale={{
                                emptyText: (
                                    <EmptyStateGuide
                                        title="Nenhum lançamento nesta fatura."
                                        description="Registre uma despesa no cartão para acompanhar a fatura do mês."
                                        actionLabel="Nova despesa"
                                        onAction={() => setIsDespesaModalOpen(true)}
                                    />
                                ),
                            }}
                            renderItem={(item) => (
                                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
                                    <Row justify="space-between" align="top" style={{ marginBottom: 4 }}>
                                        <Col span={16}>
                                            <Text strong style={{ fontSize: 15, display: 'block', lineHeight: 1.2 }}>
                                                {item.item_type?.startsWith('transacao_projetada') && <SyncOutlined style={{ marginRight: 6, fontSize: 12, color: '#50B0A0' }} />}
                                                {item.descricao}
                                            </Text>
                                            <Space split={<Divider type="vertical" />} style={{ fontSize: 12, color: 'var(--color-text-secondary, #5A6A6A)', marginTop: 4 }}>
                                                <span>{item.categoria_nome || 'Sem Categoria'}</span>
                                                <span>{item.portador || 'Titular'}</span>
                                            </Space>
                                        </Col>
                                        <Col span={8} style={{ textAlign: 'right' }}>
                                            <Text strong style={{ color: item.tipo === 'despesa' ? '#D64545' : '#2F9E6F', fontSize: 15 }}>
                                                {formatCurrency(item.valor)}
                                            </Text>
                                            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                                                {(() => {
                                                    const data = dataExibicao(item);
                                                    return data
                                                        ? new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                                                        : '—';
                                                })()}
                                            </div>
                                        </Col>
                                    </Row>
                                    
                                    {fatura?.status !== 'PAGA' && (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                            <Space size="small">
                                                {item.item_type?.startsWith('transacao_projetada') && (
                                                    <Button icon={<CheckCircleFilled />} onClick={() => handleConfirmarProjetado(item)} size="small" type="text" style={{ color: 'green' }} />
                                                )}
                                                <Button icon={<EditOutlined />} onClick={() => handleEditClick(item)} size="small" type="text" />
                                                <Button icon={<DeleteOutlined />} danger onClick={() => handleDeleteClick(item)} size="small" type="text" />
                                            </Space>
                                        </div>
                                    )}
                                </div>
                            )}
                        />
                    ) : (
                        <Table 
                            columns={columns} 
                            dataSource={filteredTransacoes} 
                            rowKey="id" 
                            pagination={{
                                ...pagination,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '25', '50', '100'],
                                size: 'small',
                            }} 
                            onChange={handleTableChange}
                            size="small" 
                        />
                    )}
                </Card>
            </Col>
            <Col xs={24} lg={8} order={isMobile ? 1 : 2}>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Card className="desempenho-hero-card" bordered={false}>
                        {fatura ? (
                            <>
                                <div style={{ marginBottom: 8 }}>{getStatusTag(fatura.status)}</div>
                                <div className="desempenho-hero-label">
                                    <DollarCircleOutlined />
                                    <span>Valor atual da fatura</span>
                                </div>
                                <div className={`desempenho-hero-value${Number(valorReal) < 0 ? ' is-negative' : ''}`}>
                                    {formatCurrency(valorReal)}
                                </div>
                                <div style={{ marginTop: 10, color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
                                    Previsto: {formatCurrency(valorReal + valorProjetado)}
                                </div>
                                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6, color: 'rgba(255,255,255,0.78)', fontSize: 12 }}>
                                    <span>Vencimento: {new Date(fatura.data_vencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</span>
                                    <span>Fechamento: {new Date(fatura.data_fechamento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</span>
                                </div>
                                <div style={{ marginTop: 16 }}>
                                    {fatura?.status === 'PAGA' ?
                                        <Button block danger ghost icon={<UndoOutlined />} onClick={() => setIsReabrirModalOpen(true)} size="large">Reabrir Fatura</Button>
                                        :
                                        <Button block type="primary" icon={<DollarCircleOutlined />} onClick={() => setIsPagarFaturaModalOpen(true)} disabled={fatura?.valor_total <= 0} size="large">Pagar Fatura Integral</Button>
                                    }
                                </div>
                            </>
                        ) : (
                            <div className="desempenho-hero-label">Sem fatura no período</div>
                        )}
                    </Card>
                    <Card title="Gastos por Portador" className="desempenho-kpi-card" bordered={false}>
                        <List 
                            size="small"
                            dataSource={gastosPortador ? [gastosPortador.titular, ...gastosPortador.adicionais] : []}
                            renderItem={item => (
                                <List.Item>
                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <span style={{ flexGrow: 1, color: 'var(--color-text-primary, #1F4F4F)' }}>{item.portador || item.nome}</span>
                                        <strong style={{ marginRight: '16px', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(item.total_gasto)}</strong>
                                        <div style={{ width: '24px', height: '24px' }}>
                                            {item.id && fatura?.status !== 'PAGA' && (
                                                <Tooltip title="Gerar Conta a Receber">
                                                    <Button type="primary" shape="circle" size="small" icon={<DollarCircleOutlined />} onClick={() => { setPortadorParaCobranca(item); setIsContaReceberModalOpen(true); }} />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </Card>
                </Space>
            </Col>
        </Row>
    );

    const tabItems = [
        { key: '1', label: 'Fatura', children: renderFaturaContent() },
        {
            key: '3',
            label: 'Análise',
            children: (
                <>
                    <UsoPorDiaChart cartaoIdFixo={cartaoId} isMobile={isMobile} />
                    <ParcelamentosFuturosChart cartaoIdFixo={cartaoId} isMobile={isMobile} />
                </>
            ),
        },
        {
            key: '2',
            label: 'Gestão de Adicionais',
            children: (
                <Card 
                    title="Cartões Adicionais" 
                    bordered={!isMobile}
                    extra={
                        <Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>Arquivados</Text>
                            <Switch size="small" checked={showArchived} onChange={setShowArchived} />
                            <Button icon={<PlusOutlined />} onClick={() => { setCartaoAdicionalToEdit(null); setIsCartaoAdicionalModalOpen(true); }} size="small">Add</Button>
                        </Space>
                    }
                >
                    <Spin spinning={loadingAdicionais}>
                        <List 
                            itemLayout="horizontal" 
                            dataSource={cartoesAdicionais}
                            renderItem={item => {
                                const isArchived = item.status === 'ARQUIVADO';
                                return (
                                    <List.Item 
                                        style={isArchived ? { background: 'var(--color-surface-muted, #F8F4EC)', opacity: 0.7 } : {}}
                                        actions={isArchived ? [<Button type="text" icon={<UndoOutlined />} onClick={() => handleReativarCartao(item)} />] : [<Button type="text" icon={<EditOutlined />} onClick={() => { setCartaoAdicionalToEdit(item); setIsCartaoAdicionalModalOpen(true); }} />, <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteCartaoAdicional(item)} />]}
                                    >
                                        <List.Item.Meta avatar={isArchived ? <InboxOutlined /> : null} title={<Space>{item.portador}{isArchived && <Tag>Arquivado</Tag>}</Space>} />
                                    </List.Item>
                                )
                            }}
                        />
                    </Spin>
                </Card>
            )
        }
    ];

    return (
        <Layout className="cartao-detalhe-container" style={{ background: 'var(--color-background, #F5F0E6)', minHeight: '100dvh' }}>
            {modalContextHolder}
            {messageContextHolder}
            
            <Card 
                className="page-header-card"
                bordered={!isMobile}
                style={{ 
                    marginBottom: isMobile ? 8 : 24, 
                    borderRadius: isMobile ? 0 : 8,
                    border: isMobile ? 'none' : undefined 
                }}
                bodyStyle={{ padding: isMobile ? '12px 16px' : '24px' }}
            >
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <Space align="center" size="small">
                            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/cartoes')} />
                            <LogoManager slug={bandeiraSlug} type="bandeira" size={32} />
                            <Dropdown
                                trigger={['click']}
                                disabled={loadingCartoes}
                                menu={{
                                    selectable: true,
                                    selectedKeys: cartaoId ? [String(cartaoId)] : [],
                                    items: cartoes.map((cartao) => ({
                                        key: String(cartao.id),
                                        label: cartao.nome,
                                    })),
                                    onClick: ({ key }) => {
                                        if (String(key) !== String(cartaoId)) {
                                            navigate(`/cartoes/${key}`);
                                        }
                                    },
                                }}
                            >
                                <Button className="cartao-header-select-btn" loading={loadingCartoes}>
                                    <span className="cartao-header-select-btn-label">
                                        {cartaoSelecionado?.nome || fatura?.cartao_nome || 'Selecionar cartão'}
                                    </span>
                                    <DownOutlined className="cartao-header-select-btn-arrow" />
                                </Button>
                            </Dropdown>
                        </Space>
                    </Col>
                    
                    {/* Header Mobile Otimizado: Empilha Seletor e Botão / Desktop alinha à direita */}
                    <Col xs={24} md={12} style={{ 
                        display: 'flex', 
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 12, 
                        justifyContent: isMobile ? 'flex-start' : 'flex-end', 
                        width: '100%' 
                    }}>
                        <div style={{ width: isMobile ? '100%' : 'auto' }}>
                            <SeletorDeMes value={currentDate} onChange={setCurrentDate} />
                        </div>
                        <Dropdown menu={menuNovoLancamento} disabled={fatura?.status === 'PAGA'} trigger={['click']}>
                            <Button type="primary" icon={<PlusOutlined />} block={isMobile}>
                                {isMobile ? 'Lançar' : 'Novo Lançamento'} <DownOutlined />
                            </Button>
                        </Dropdown>
                    </Col>
                </Row>
            </Card>

            <Content 
                style={{ 
                    padding: isMobile ? '0' : '0 24px 24px', 
                    overflowX: 'hidden', // Evita scroll horizontal
                    maxWidth: '100vw'    // Garante que não passe da largura da tela
                }}
            >
                <Spin spinning={loading || !currentDate} tip="Carregando...">
                    {currentDate && <Tabs defaultActiveKey="1" items={tabItems} type="card" className="sa-page-tabs" size={isMobile ? 'large' : 'middle'} />}
                </Spin>
            </Content>

            {/* --- MODAIS --- */}
            <AntModal
                title="Reabrir Fatura"
                open={isReabrirModalOpen}
                onCancel={() => !reabrindoFatura && setIsReabrirModalOpen(false)}
                onOk={handleConfirmReabrirFatura}
                okText="Sim, Reabrir"
                cancelText="Cancelar"
                confirmLoading={reabrindoFatura || loadingPontosReabrir}
                okButtonProps={{ danger: true, disabled: loadingPontosReabrir, block: isMobile, size: isMobile ? 'large' : 'middle' }}
                cancelButtonProps={{ block: isMobile, size: isMobile ? 'large' : 'middle' }}
                {...getMobileFullscreenModalProps(isMobile)}
                width={isMobile ? undefined : 520}
            >
                <p>A reabertura da fatura irá estornar o valor do pagamento na sua conta de origem.</p>
                {pontosPendentesReabrir.length > 0 && (
                    <div style={{ marginTop: 12, marginBottom: 8 }}>
                        <p>
                            Existe{pontosPendentesReabrir.length > 1 ? 'm' : ''}{' '}
                            {pontosPendentesReabrir.length} lançamento
                            {pontosPendentesReabrir.length > 1 ? 's' : ''} pendente
                            {pontosPendentesReabrir.length > 1 ? 's' : ''} de{' '}
                            {pontosPendentesReabrir
                                .reduce((acc, tx) => acc + (Number(tx.quantidade_milhas) || 0), 0)
                                .toLocaleString('pt-BR')}{' '}
                            milhas desta fatura.
                        </p>
                        <Checkbox
                            checked={excluirPontosPendentes}
                            onChange={(e) => setExcluirPontosPendentes(e.target.checked)}
                        >
                            Excluir lançamento de milhas pendente
                        </Checkbox>
                    </div>
                )}
                <p>Deseja continuar?</p>
            </AntModal>
            
            <AntModal title="Editar Compra Parcelada" open={isEditScopeModalOpen} onCancel={() => setIsEditScopeModalOpen(false)} footer={null} {...getMobileFullscreenModalProps(isMobile)} width={isMobile ? undefined : 520}>
                <p>Como você deseja aplicar esta alteração?</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Button block onClick={() => { setIsEditScopeModalOpen(false); setEditScope('apenas_esta'); setIsDespesaModalOpen(true); }}>Apenas Esta Parcela</Button>
                    <Button block type="primary" onClick={() => { setIsEditScopeModalOpen(false); setEditScope('esta_e_as_proximas'); setIsDespesaModalOpen(true); }}>Esta e as Próximas</Button>
                    <Button block onClick={() => setIsEditScopeModalOpen(false)}>Cancelar</Button>
                </div>
            </AntModal>
            
            <AntModal title="Excluir Compra Parcelada" open={isDeleteScopeModalOpen} onCancel={() => setIsDeleteScopeModalOpen(false)} footer={null} {...getMobileFullscreenModalProps(isMobile)} width={isMobile ? undefined : 520}>
                <p>Como você deseja aplicar esta exclusão?</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Button block danger onClick={() => { setIsDeleteScopeModalOpen(false); performDelete(transacaoToEdit.id, 'apenas_esta'); }}>Apenas Esta Parcela</Button>
                    <Button block type="primary" danger onClick={() => { setIsDeleteScopeModalOpen(false); performDelete(transacaoToEdit.id, 'esta_e_as_proximas'); }}>Esta e as Próximas</Button>
                    <Button block onClick={() => setIsDeleteScopeModalOpen(false)}>Cancelar</Button>
                </div>
            </AntModal>

            <CartaoAdicionalModalAntD isOpen={isCartaoAdicionalModalOpen} onClose={handleCloseCartaoAdicionalModal} onSave={handleCloseCartaoAdicionalModal} cartaoPrincipalId={cartaoId} cartaoToEdit={cartaoAdicionalToEdit} />
            <GerarContaReceberModalAntD isOpen={isContaReceberModalOpen} onClose={handleCloseContaReceberModal} onSave={handleCloseContaReceberModal} fatura={fatura} portador={portadorParaCobranca} />
            <PagarFaturaModal isOpen={isPagarFaturaModalOpen} onClose={() => setIsPagarFaturaModalOpen(false)} onSave={handleCloseAndRefresh} fatura={fatura} transacoes={transacoes} isMobile={isMobile} />
            <PagamentoParcialModal isOpen={isPagamentoParcialModalOpen} onClose={() => { setIsPagamentoParcialModalOpen(false); setTransacaoToEdit(null); }} onSave={handleCloseAndRefresh} fatura={fatura} transacaoToEdit={transacaoToEdit} isMobile={isMobile} />
            <CartaoDespesaModal isOpen={isDespesaModalOpen} onClose={() => handleCloseAndRefresh(true)} onSave={handleCloseAndRefresh} transacaoToEdit={transacaoToEdit} cartaoId={cartaoId} faturaSugeridaId={fatura ? fatura.id : null} escopoDaAlteracao={editScope} isMobile={isMobile} allCategorias={categorias} />
            <EstornoModal isOpen={isEstornoModalOpen} onClose={handleCloseAndRefresh} onSave={handleCloseAndRefresh} cartaoId={cartaoId} faturaSugeridaId={fatura ? fatura.id : null} transacaoToEdit={transacaoToEdit} isMobile={isMobile} />
            {editingProjetado && (
                <ProjetadaEditModal isOpen={isProjetadaModalOpen} onClose={() => setIsProjetadaModalOpen(false)} onSave={() => { setIsProjetadaModalOpen(false); carregarDadosFatura(currentDate); }} projetado={editingProjetado} isMobile={isMobile} />
            )}
        </Layout>
    );
};

export default CartaoDetailPage;